'use strict';

import ViewPortOverlay from './ViewPortOverlay.js';

const av = Autodesk.Viewing;
const GlobalManagerMixin = av.GlobalManagerMixin;

const DROPME_MODE = {auto: 'Auto', manual: 'Manual'};

export default class DropMeTool extends av.ToolInterface {
    constructor(viewer, onDrop, extension) {
        super();

        this.names = ['drop-me'];
        this.viewer = viewer;
        this.setGlobalManager(this.viewer.globalManager);
        this.extension = extension;
        this.options = extension.options;
        if (this.options.teleportOnRelease === undefined) {
            this.options.teleportOnRelease = true; // Set true by default
        }
        this.viewer.toolController.registerTool(this);
        this.onDrop = onDrop;
        this.active = false;
        this._movedSinceFirstClick = false;

        this.init();

        this.cameraSelectionState = {
            pending: false,
            pos:     null,
            target:  null,
            inViewport: false,
            initialVp: null
        };

        this.teleportationInProgress = 0;

        // 'False' indicates if the user has clicked a first time alredy to choose position, so that the
        // next click will determine view-direction and instantly invoke the transition.
        this.isFirstClick = true;

        this.viewPortOverlay = new ViewPortOverlay(this.viewer);
        this.escapeHotkeyId = 'Autodesk.AEC.DropMeToolEsc';
    }

    init() {

        this.activate = () => {
            const gestureHandler = this.viewer.toolController.getTool("gestures");
            if (gestureHandler) {
                gestureHandler.setGestureParameter('drag', 'threshold', 0);
            }

            const hotkeys = [{
                keycodes: [
                    Autodesk.Viewing.KeyCode.ESCAPE
                ],
                onRelease: () => this.options.triggerDeactivate()
            }];
            this.viewer.getHotkeyManager().pushHotkeys(this.escapeHotkeyId, hotkeys);
            this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this.options.triggerDeactivate);
            this.viewer.impl.pauseHighlight(true);
            this.active = true;
        };

        this.deactivate = ()=> {
            const gestureHandler = this.viewer.toolController.getTool("gestures");
            if (gestureHandler) {
                gestureHandler.restoreGestureParameterDefault('drag', 'threshold');
            }

            this.viewer.getHotkeyManager().popHotkeys(this.escapeHotkeyId);
            this._cancelTeleportation();
            this._destroyCameraSelectorUi();
            this.viewer.removeEventListener(av.MODEL_ADDED_EVENT, this.options.triggerDeactivate);
            this.viewer.impl.pauseHighlight(false);
            this.active = false;
            this._movedSinceFirstClick = false;
        };

        this.handleButtonDown = function () {
            return true;
        };

        this.handleButtonUp = function () {
            if (!this._movedSinceFirstClick || this.isFirstClick) {
                return false;
            }
            return true;
        };

        this.handleSingleClick = (e, isTouch) => {
            if (this.isFirstClick) {
                const tmpres = this._get3DCameraPositionAndVp(e.canvasX, e.canvasY);
                if (tmpres && tmpres.pos) {
                    this.cameraSelectionState.pos = {x: e.canvasX, y: e.canvasY};
                    this.cameraSelectionState.pending = true;
                    this.cameraSelectionState.initialVp = tmpres.vp;

                    this._createCameraSelectorUi(this.cameraSelectionState.pos);

                    requestAnimationFrame(() => {
                        // We should only trigger animation if mouse has not been moved since the first click.
                        if (!this._movedSinceFirstClick)
                            this.cameraSelectorPosition.classList.add('camera-selector-position--teleporting');
                    });

                    if (tmpres.vp.isTopViewViewport() && !isTouch) {
                        // while teleportation is pending this.handleMouseMove will move camera frustum
                        this._scheduleTeleportation();

                        // needed only for scheduled teleportation
                        this.isFirstClick = false;
                        this.cursor = 'none';
                        this.getCursor();
                    }
                    else {
                        this._runTeleportation(DROPME_MODE.auto);
                    }
                }
            }
            else {
                if (this.options.teleportOnRelease) {
                    this.teleport();
                }
            }

            // As expectation of single click is a teleportation to another view, we simply consume event and do not propogate it further
            return true;
        };

        this.teleport = () => {
            this.cameraSelectionState.target = {x: this.lastX, y: this.lastY};
            this._runTeleportation(DROPME_MODE.manual);
            this.isFirstClick = this.options.teleportOnRelease;
            this._movedSinceFirstClick = false;
            this._recoverCursor();
        };

        this.handleMouseMove = (e) => {
            if (!this.isFirstClick && !this._movedSinceFirstClick)
                this._movedSinceFirstClick = true;

            if (this.cursor)
                this._recoverCursor();

            const tmpres = this._get3DCameraPositionAndVp(e.canvasX, e.canvasY);

            if (this.cameraSelectionState.initialVp && !this.isFirstClick) {
                this._addVpOverlay(this.cameraSelectionState.initialVp);
            }
            else if (tmpres && tmpres.pos) {
                this._addVpOverlay(tmpres.vp);
            }
            else {
                this.viewPortOverlay.removeOverlay();
            }

            this.cameraSelectionState.inViewport = (this.isFirstClick && tmpres && tmpres.pos) || !this.isFirstClick;
            this.getCursor();

            if (!this.cameraSelectionState.pending) {
                return;
            }

            if (this.teleportationInProgress) {
                // cancel position animation
                this.cameraSelectorPosition.classList.remove('camera-selector-position--teleporting');
                clearTimeout(this.teleportationInProgress);
                this.teleportationInProgress = 0;
            }

            this.lastX = e.canvasX;
            this.lastY = e.canvasY;
            const dx = e.canvasX - this.cameraSelectionState.pos.x;
            const dy = e.canvasY - this.cameraSelectionState.pos.y;
            let angle = Math.atan2(dy, dx) * 180 / Math.PI;

            // In case the mouse moved:
            if (dx !== 0 || dy !== 0) {
                // atan measures the angle to the x-axis, but the frustum gizmo is originally aligned with y.
                angle += 90;
            }

            this.cameraSelector.style.transform = `rotate(${angle}deg)`;

            if (!this.options.teleportOnRelease) {
                this.teleport();
            }

            //console.log(angle);
            return true;
        };

        this.handleSingleTap = function( event ) {
            return this.handleSingleClick(event, true);
        };

        this.handleResize = () => {
            // As teleportation is going to be rather short interaction with a short-living UI, we just do not handle screen resize at all for now, but deactivate tool instead.
            this.options.triggerDeactivate();
        };

        this.handleSingleTap = (event) => {
            if (this.isFirstClick) {
                this.handleSingleClick(event);
            } else {
                this.handleMouseMove(event);
            }
        };

        this.handleGesture = (event) => {
            switch (event.type) {
                case 'dragstart':
                    break;
                case 'dragmove':
                    return this.handleMouseMove(event, 0);
                case 'dragend':
                    return true;
                case 'panmove':
                case 'panstart':
                case 'panend':
                case 'pinchmove':
                case 'pinchstart':
                case 'pinchend':
                    return true;
                default:
                    break;
            }

            return false;
        };
    }

    _recoverCursor() {
        this.cursor = null;
        this.getCursor();
    }

    getCursor() {
        if (this.cursor)
            return this.cursor;
        return this.cameraSelectionState.inViewport ? 'cell' : 'default';
    }

    _addVpOverlay(vp) {
        this.viewPortOverlay.removeOverlay();
        const vpRect = vp.getViewportBounds(this.viewer.model.getUnitScale());
        const min = vpRect.min;
        const max = vpRect.max;
        this.viewPortOverlay.addOverlay(min.x, min.y, max.x, max.y);
    }

    // mode is only required for analytics purposes
    _runTeleportation(mode) {
        // Use this event in order to show a loading spinner until the drop-me is done.
        this.extension.dispatchEvent({ type: Autodesk.AEC.DropMeExtension.DROP_ME_STARTED_EVENT });

        this._mapToCameraPositionIn3D(mode);

        // Although _mapToCameraPositionIn3D runs async functions, we do want to call triggerDeactivate right now,
        // Since it might take a few seconds until we'll get all the required data in order to load the 3D model.
        // Until then, disable button until dropping into the 3D model.
        this.options.triggerDeactivate(true);
    }

    _scheduleTeleportation() {
        this.teleportationInProgress = setTimeout(() => {
            this._runTeleportation(DROPME_MODE.auto);
        }, 3000); // this timeout is alligned with coresponding rule of camera-selector-position css class
    }

    _cancelTeleportation() {
        if (this.teleportationInProgress) {
            clearTimeout(this.teleportationInProgress);
            this.teleportationInProgress = 0;
        }
    }

    _createCameraSelectorUi(pos) {
        //console.log(`camera position at ${pos.x}, ${pos.y}`);
        const _document = this.getDocument();
        this.cameraSelectorBackground = _document.createElement('div');
        this.cameraSelectorBackground.className = 'camera-selector-background';
        this.viewer.container.appendChild(this.cameraSelectorBackground);
        this.cameraSelectorBackground.style.top = `${pos.y}px`;
        this.cameraSelectorBackground.style.left = `${pos.x}px`;

        this.cameraSelectorPosition = _document.createElement('div');
        this.cameraSelectorPosition.className = 'camera-selector-position';
        this.viewer.container.appendChild(this.cameraSelectorPosition);
        this.cameraSelectorPosition.style.top = `${pos.y-10}px`;
        this.cameraSelectorPosition.style.left = `${pos.x-10}px`;

        this.cameraSelector = _document.createElement('div');
        this.cameraSelector.className = 'camera-selector';
        this.viewer.container.appendChild(this.cameraSelector);
        this.cameraSelector.style.top = `${pos.y-100}px`;
        this.cameraSelector.style.left = `${pos.x-50}px`;
    }

    _destroyCameraSelectorUi() {
        if (this.viewer.container.contains(this.cameraSelector)) {
            this.viewer.container.removeChild(this.cameraSelector);
        }

        if (this.viewer.container.contains(this.cameraSelectorBackground)) {
            this.viewer.container.removeChild(this.cameraSelectorBackground);
        }

        if (this.viewer.container.contains(this.cameraSelectorPosition)) {
            this.viewer.container.removeChild(this.cameraSelectorPosition);
        }

        this.cameraSelectionState = {
            pending: false,
            pos:     null,
            target:  null,
            inViewport: false,
            initialVp: null
        };

        this.isFirstClick = true;

        this.viewPortOverlay.removeOverlay();
    }

    // Given F2D world coords (Vec3), this function returns the viewport that was hit.
    _findViewport(f2dWorldPos) {
        const p2d  = new THREE.Vector2(f2dWorldPos.x, f2dWorldPos.y);
        const sheet = this.viewer.model;

        // onlyTopViewPlans is currently true by default anyway, but it's better to explicitly put it here so it will be clear that we request only top view viewports.
        const viewport = this.extension.viewportsExtension.findViewportAtPoint(sheet, p2d, { onlyTopViewPlans: true });

        if (this.options.useOnlyAecModelDataViewports && !(viewport instanceof Autodesk.AEC.ViewportsExtension.AecModelDataViewport)) {
            return null;
        }

        return viewport;
    }

    // Find or load otg model root for the given BubbleNode
    async _getOtgRoot(node) {
        const model = this.viewer.getAllModels().find((m) => {
            const modelNode = m.getDocumentNode();
            return modelNode == node;
        });

        // The friendly case - we have the main 3D model in memory
        if (model) {
            return model.myData;
        }

        // The ugly case - we have to load the model root first
        const root = node.getRootNode();
        const doc = root.getDocument();
        const path = doc.getViewablePath(node);
        const options = { acmSessionId: root.urn() };
        const otgRoot = await Autodesk.Viewing.Private.OtgLoader.loadOnlyOtgRoot(path, options);
        return otgRoot; // null if loading fails for whatever reason
    }

    // Find out main model units (in model-coordinates, excluding any load-transforms)
    async _getModelUnitsFromNode(node) {
        const otgRoot = await this._getOtgRoot(node);
        if (otgRoot) {
            // Note that the app may apply unitScaling on load which overrides the units.
            // So, it's  essential to use the metadata to get the original file units.
            const distanceUnit = otgRoot.metadata?.['distance unit']?.value;
            if (distanceUnit) {
                return distanceUnit;
            }
        }

        // If we don't find anything better, assume feet by default
        // (matches with Revit models and legacy behavior)
        return 'ft';
    }

    // get scale factor to convert from meters to units in model coordinates (excluding load transforms)
    async _getMeterToModelScale(node) {
        const unit = await this._getModelUnitsFromNode(node);
        const unitData = Autodesk.Viewing.Private.getUnitData(unit);
        return 1.0 / unitData.unitScale;
    }

    async _mapToCameraPositionIn3DForFloorPlan(vp, pos, mode) {
        const sheetNode = this.viewer.model.getDocumentNode();
        let main3DNode = this.options.getMain3DView?.(sheetNode);

        // Store target here before reaching to the async parts - to make sure that it won't get cleared until we'll need to use it.
        const target = this.cameraSelectionState.target;

        if (!main3DNode) {
            main3DNode = (await vp.get3DNodesForViewport())[0];
        }


        // add avatar height of 1.75 meters above the ground of the current level.
        // Note that at this point, position is still in local model coords, i.e., independent of client-specific load transforms.
        const level = vp.findLevelForViewport();
        const isFloorPlan = vp.isFloorPlan();
        if (level && isFloorPlan) {
            // Get scale factor to convert from meter to units in local 3D model coords
            const meterToModel = await this._getMeterToModelScale(main3DNode);

            // Offset by an avatar height of 1.75 meters
            const avatarHeight = 1.75 * meterToModel;
            pos.z = level.elevation + avatarHeight;
        }

        let posGeorefed = pos;

        if (vp.isMatrixInLocalCoords()) {
            posGeorefed = await this._applyModelToViewerTransform(pos, main3DNode);
        }

        let dir = new THREE.Vector3(0.0, 1.0, 0.0);

        // in the case of not specified target, use north direction
        if (target) {
            const x = target.x;
            const y = target.y;

            const resTarget = this._get3DCameraPositionAndVp(x, y, true);
            if (resTarget && resTarget.pos) {
                let targetGeorefed = resTarget.pos;

                if (vp.isMatrixInLocalCoords()) {
                    targetGeorefed = await this._applyModelToViewerTransform(resTarget.pos, main3DNode);
                }

                const dirX = targetGeorefed.x - posGeorefed.x;
                const dirY = targetGeorefed.y - posGeorefed.y;

                if (dirX !== 0 || dirY !== 0) {
                    dir.setX(dirX).setY(dirY);
                    dir.normalize();
                }
            }
        }

        // mimic natural looking direction which is not really parallel to the ground
        const rotAxis = new THREE.Vector3(-dir.y, dir.x, 0.0);
        dir.applyAxisAngle(rotAxis, Math.PI/18.0); // 10 degrees

        this.onDrop(posGeorefed, dir, mode, main3DNode, !vp.isMatrixInLocalCoords());
    }

    // Notice that this path is currently not reachable, since `filterAndFlattenViewports` returns only topViewViewports by default.
    // If a section viewport support is required in the future, just supply different flags to it.
    async _mapToCameraPositionIn3DForSection(vp, pos, mode) {
        const sheetNode = this.viewer.model.getDocumentNode();
        let main3DNode = this.options.getMain3DView?.(sheetNode);

        if (!main3DNode) {
            main3DNode = (await vp.get3DNodesForViewport())[0];
        }

        let posGeorefed = pos;
        if (vp.isMatrixInLocalCoords()) {
            pos = await this._applyModelToViewerTransform(pos, main3DNode);
        }

        // by default we are looking into the north direction, in this case "into the screen"
        let dir = new THREE.Vector3(0.0, 1.0, 0.0);

        // mimic natural looking direction which is not really parallel to the ground
        const rotAxis = new THREE.Vector3(-dir.y, dir.x, 0.0);
        dir.applyAxisAngle(rotAxis, Math.PI/18.0); // 10 degrees

        this.onDrop(posGeorefed, dir, mode, main3DNode, !vp.isMatrixInLocalCoords());
    }

    // if we will come up with a good visualization widget for dir selection for "Section" views
    // the two methods "_mapToCameraPositionIn3DForFloorPlan()" and "_mapToCameraPositionIn3DForSection()"
    // could be unified. we will still have two cases in "if (resTarget && resTarget.pos) {...}" code path
    //   1. FloorPlan: x,y - change, z - constant
    //   2. Section:   x - changes, y,z - constant
    _mapToCameraPositionIn3D(mode = DROPME_MODE.manual) {
        const x = this.cameraSelectionState.pos.x;
        const y = this.cameraSelectionState.pos.y;
        const resPos = this._get3DCameraPositionAndVp(x, y);

        if (resPos && resPos.pos && resPos.vp) {
            if (resPos.vp.isTopViewViewport()) {
                this._mapToCameraPositionIn3DForFloorPlan(resPos.vp, resPos.pos, mode);
            } else {
                this._mapToCameraPositionIn3DForSection(resPos.vp, resPos.pos, DROPME_MODE.auto);
            }

        }
    }

    _get3DCameraPositionAndVp(x, y, useInitialVp = false) {
        const vpVec = this.viewer.impl.clientToViewport(x, y);
        const point = this.viewer.impl.intersectGroundViewport(vpVec);

        // if no viewport can be found for the current mouse position on a second click, try to use initial viewport
        const hitViewport = this._findViewport(point) || (useInitialVp && this.cameraSelectionState.initialVp);
        if (!hitViewport) {
            // Clicked outside of the viewport
            // DropMe tool remains active and allows user to pick another point
            return null;
        }

        // Get unitScale for current sheet
        const sheetUnitScale = this.viewer.model.getUnitScale();

        const pos = this.sheetTo3DWorld(hitViewport, point, sheetUnitScale);

        return {
            pos,
            vp: hitViewport,
        };
    }

    sheetTo3DWorld(viewport, point, sheetUnitScale) {
        // Apply 2D->3D transform to get 3D world coords
        const vpTransform = viewport.get2DTo3DMatrix(sheetUnitScale);
        const pWorld = point.clone().applyMatrix4(vpTransform);
        return pWorld;
    }

    // Default function that extracts refPointTransform from the root node.
    async _applyModelToViewerTransform(pos, bubbleNode) {
        let vec = pos.clone();

        // Skip refPoint transform if wanted
        if (this.options.useLocalModelCoords) {
            return vec;
        }

        let matrix;

        if (bubbleNode && this.options.getTransformForNode) {
            // Notice that matrix could still be null after calling getTransformForNode:
            // in case of a problem with the service / transform doesn't exist there.
            matrix = await this.options.getTransformForNode(bubbleNode);
        }

        if (!matrix) {
            // This extension requires AEC model data, so there should always be a refPoint tranform
            const root = this.viewer.model.getDocumentNode().getRootNode();
            matrix = root.extractRefPointTransform();
        }

        vec.applyMatrix4(matrix);

        return vec;
    }
}

GlobalManagerMixin.call(DropMeTool.prototype);
